<script lang="ts" setup>
import { computed, ref, useTemplateRef, useId, watch } from 'vue';
import VToggleSwitchLabel from './VToggleSwitchLabel.vue';

type Props = {
  isActive?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
} & (
  | { labelInactive?: undefined; labelActive?: undefined }
  | { labelInactive: string; labelActive: string }
);

const props = withDefaults(defineProps<Props>(), {
  size: 'sm',
});

const emit = defineEmits<{
  change: [isActive: boolean];
  'update:isActive': [value: boolean];
}>();

const showActive = ref(props.isActive);

watch(
  () => props.isActive,
  (val) => {
    showActive.value = val;
  },
);

function emitChange(value: boolean) {
  emit('change', value);
  emit('update:isActive', value);
}

const id = useId();
const inactiveId = `${id}-inactive`;
const activeId = `${id}-active`;

const inactiveButtonRef =
  useTemplateRef<InstanceType<typeof VToggleSwitchLabel>>('inactiveButtonRef');
const activeButtonRef = useTemplateRef<InstanceType<typeof VToggleSwitchLabel>>('activeButtonRef');

const hasLabels = computed(() => !!props.labelInactive);

const sizeClasses = computed(() => {
  const sizes = { sm: 'text-sm h-8', md: 'text-base h-9', lg: 'text-lg h-10' };
  return sizes[props.size];
});

const backgroundStyle = computed(() => {
  const inactiveEl = inactiveButtonRef.value?.el;
  const activeEl = activeButtonRef.value?.el;

  if (!inactiveEl || !activeEl) return {};

  const width = showActive.value ? activeEl.offsetWidth : inactiveEl.offsetWidth;
  const translateX = showActive.value ? Math.abs(inactiveEl.offsetLeft - activeEl.offsetLeft) : 0;
  return {
    width: `${width}px`,
    translate: `${translateX}px`,
  };
});
</script>

<template>
  <div
    v-if="hasLabels"
    class="min-h- relative inline-flex gap-1 rounded bg-grey-200/30 p-1"
    role="radiogroup"
    :aria-label="`Toggle tussen ${labelInactive} en ${labelActive}`"
  >
    <div
      :class="['rounded-md bg-white shadow-sm', 'absolute top-1 bottom-1 left-1', 'duration-200']"
      :style="backgroundStyle"
      aria-hidden="true"
    ></div>

    <VToggleSwitchLabel
      ref="inactiveButtonRef"
      v-model="showActive"
      :label-for="inactiveId"
      :name="id"
      :value="false"
      :label="labelInactive!"
      :disabled="disabled"
      :size-classes="sizeClasses"
      @update:model-value="emitChange"
    />
    <VToggleSwitchLabel
      ref="activeButtonRef"
      v-model="showActive"
      :label-for="activeId"
      :name="id"
      :value="true"
      :label="labelActive!"
      :disabled="disabled"
      :size-classes="sizeClasses"
      @update:model-value="emitChange"
    />
  </div>

  <label
    v-else
    :for="id"
    class="inline-flex cursor-pointer"
    :class="{ 'cursor-not-allowed opacity-50': disabled }"
  >
    <input
      :id="id"
      v-model="showActive"
      type="checkbox"
      :disabled="disabled"
      class="sr-only"
      @change="emitChange(showActive)"
    />
    <span
      :class="[
        'block h-[1.8em] em-w-12 rounded-full bg-grey-400 p-[.2em] transition-colors duration-300',
        { 'bg-primary': showActive },
      ]"
    >
      <span
        :class="[
          'block size-[1.4em] rounded-full bg-white transition-transform duration-[inherit]',
          { 'translate-x-[1.2em]': showActive },
        ]"
      ></span>
    </span>
  </label>
</template>
